import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, FlaskConical, ImagePlus, Plus, Trash2, Users, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { api, formatApiError } from '../lib/api';
import { fieldCls, labelCls } from '../components/member/ProfileForm';

const empty = { subject: '', heading: '', subheading: '', body: '', image_url: null, links: [] };

const CampaignRow = ({ c }) => (
  <div className="rounded-2xl bg-[#1a1424] border border-white/5 p-4 flex flex-wrap items-center gap-3" data-testid={`campaign-${c.id}`}>
    <div className="flex-1 min-w-[200px]">
      <p className="text-white font-bold">{c.subject}</p>
      <p className="text-white/40 text-xs">{new Date(c.created_at).toLocaleString('nb-NO')} · {c.sent_by}</p>
    </div>
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.status === 'sent' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-[#ffb400]/15 text-[#ffb400]'}`}>
      {c.status === 'sent' ? 'Sendt' : 'Sender…'} {c.sent}/{c.total}{c.failed ? ` · ${c.failed} feila` : ''}
    </span>
  </div>
);

const AdminNewsletter = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [subs, setSubs] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login', { replace: true });
    else if (!user.is_admin) navigate('/medlem', { replace: true });
  }, [user, loading, navigate]);

  const loadCampaigns = () => api.get('/admin/newsletter/campaigns').then((r) => setCampaigns(r.data)).catch(() => {});

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get('/admin/newsletter').then((r) => setSubs(r.data.length)).catch(() => {});
    loadCampaigns();
  }, [user]);

  useEffect(() => {
    if (!user?.is_admin || !form.heading) { setPreview(''); return; }
    const id = setTimeout(() => {
      api.post('/admin/newsletter/preview', { ...form, subject: form.subject || form.heading }).then((r) => setPreview(r.data.html)).catch(() => {});
    }, 400);
    return () => clearTimeout(id);
  }, [form, user]);

  useEffect(() => {
    if (!campaigns.some((c) => c.status === 'sending')) return;
    const id = setInterval(loadCampaigns, 3000);
    return () => clearInterval(id);
  }, [campaigns]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLink = (i, k, v) => setForm({ ...form, links: form.links.map((l, j) => (j === i ? { ...l, [k]: v } : l)) });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('image');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/newsletter/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success('Biletet er lasta opp');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy('');
      e.target.value = '';
    }
  };

  const send = async (testOnly) => {
    if (!form.subject || !form.heading) return toast.error('Fyll inn emne og overskrift.');
    if (!testOnly && !window.confirm(`Sende til alle ${subs} abonnentane no?`)) return;
    setBusy(testOnly ? 'test' : 'send');
    try {
      const { data } = await api.post('/admin/newsletter/send', { ...form, links: form.links.filter((l) => l.label && l.url), test_only: testOnly });
      if (testOnly) toast.success(`Test-e-post sendt til ${data.to}`);
      else { toast.success('Utsendinga har starta!'); setForm(empty); loadCampaigns(); }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy('');
    }
  };

  if (!user?.is_admin) return <div className="min-h-screen bg-black" />;

  return (
    <div className="bg-black min-h-screen text-white" data-testid="admin-newsletter-page">
      <Seo title="Nyheitsbrev – admin | Refuel Weekend" description="Send nyheitsbrev." path="/admin/nyheitsbrev" noindex />
      <Navbar />
      <main className="max-w-[1300px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/admin" data-testid="newsletter-back-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm mb-8"><ArrowLeft size={16} /> Adminpanel</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2">Admin</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>Send nyheitsbrev</h1>
          </div>
          <div className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0f0a17] px-5 py-3" data-testid="subscriber-count">
            <Users size={20} className="text-[#ffb400]" />
            <span className="text-2xl font-bold" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{subs ?? '–'}</span>
            <span className="text-white/50 text-sm">abonnentar</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <form onSubmit={(e) => { e.preventDefault(); send(false); }} className="space-y-4" data-testid="newsletter-compose-form">
            <div><label className={labelCls}>Emne (i innboksen)</label><input data-testid="nl-subject" value={form.subject} onChange={set('subject')} className={fieldCls} maxLength={120} placeholder="Nytt frå Refuel Weekend" required /></div>
            <div><label className={labelCls}>Overskrift</label><input data-testid="nl-heading" value={form.heading} onChange={set('heading')} className={fieldCls} maxLength={120} placeholder="Konsertartisten er klar!" required /></div>
            <div><label className={labelCls}>Undertekst</label><input data-testid="nl-subheading" value={form.subheading} onChange={set('subheading')} className={fieldCls} maxLength={200} placeholder="Kort ingress under overskrifta" /></div>
            <div><label className={labelCls}>Tekst</label><textarea data-testid="nl-body" value={form.body} onChange={set('body')} rows={7} className={`${fieldCls} resize-y`} maxLength={5000} placeholder="Skriv teksten her. Tom linje = nytt avsnitt." /></div>

            <div>
              <label className={labelCls}>Bilete</label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="nl-image-input" />
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy === 'image'} data-testid="nl-image-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white font-semibold text-sm hover:border-[#ff2e2e] hover:text-[#ff2e2e] transition-colors disabled:opacity-50">
                  {busy === 'image' ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} {form.image_url ? 'Byt bilete' : 'Last opp bilete'}
                </button>
                {form.image_url && (
                  <>
                    <img src={form.image_url} alt="" className="h-12 w-20 object-cover rounded-lg border border-white/10" data-testid="nl-image-preview" />
                    <button type="button" onClick={() => setForm({ ...form, image_url: null })} aria-label="Fjern bilete" className="w-9 h-9 rounded-full border border-white/10 text-white/50 hover:text-red-400 hover:border-red-500 flex items-center justify-center"><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls}>Lenker / knappar</label>
                {form.links.length < 5 && (
                  <button type="button" onClick={() => setForm({ ...form, links: [...form.links, { label: '', url: 'https://' }] })} data-testid="nl-add-link" className="inline-flex items-center gap-1 text-xs font-bold text-[#ff2e2e] hover:underline"><Plus size={14} /> Legg til lenke</button>
                )}
              </div>
              <div className="space-y-2">
                {form.links.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1.5fr_auto] gap-2">
                    <input value={l.label} onChange={(e) => setLink(i, 'label', e.target.value)} className={fieldCls} placeholder="Knappetekst" maxLength={60} data-testid={`nl-link-label-${i}`} />
                    <input value={l.url} onChange={(e) => setLink(i, 'url', e.target.value)} className={fieldCls} placeholder="https://…" type="url" data-testid={`nl-link-url-${i}`} />
                    <button type="button" onClick={() => setForm({ ...form, links: form.links.filter((_, j) => j !== i) })} aria-label="Fjern lenke" className="w-12 rounded-2xl border border-white/10 text-white/50 hover:text-red-400 hover:border-red-500 flex items-center justify-center"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button type="button" onClick={() => send(true)} disabled={!!busy} data-testid="nl-send-test" className="flex-1 py-3.5 rounded-full border-2 border-[#ffb400] text-[#ffb400] font-bold uppercase tracking-wide hover:bg-[#ffb400] hover:text-black transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy === 'test' ? <Loader2 size={16} className="animate-spin" /> : <FlaskConical size={16} />} Send test til meg
              </button>
              <button type="submit" disabled={!!busy || !subs} data-testid="nl-send-all" className="flex-1 py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy === 'send' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send til alle ({subs ?? 0})
              </button>
            </div>
          </form>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-widest"><Eye size={14} /> Førehandsvising</div>
            <div className="rounded-3xl overflow-hidden border border-white/10 bg-[#0b0611] min-h-[400px]" data-testid="nl-preview">
              {preview ? (
                <iframe title="Førehandsvising" srcDoc={preview} className="w-full h-[640px] bg-transparent" sandbox="" />
              ) : (
                <div className="h-[400px] flex items-center justify-center text-white/30 text-sm">Skriv ei overskrift for å sjå førehandsvisinga</div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="text-lg md:text-xl font-bold mb-4">Tidlegare utsendingar</h2>
          <div className="space-y-2" data-testid="campaign-list">
            {campaigns.length === 0 ? <p className="text-white/40 text-sm">Ingen utsendingar enno.</p> : campaigns.map((c) => <CampaignRow key={c.id} c={c} />)}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminNewsletter;
